<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="10" lg="8">
        <v-card>
          <v-card-title>
            <v-icon left color="primary">mdi-email-outline</v-icon>
            Class Reminders
          </v-card-title>
          <v-card-subtitle>
            Configure when and how parents get automated reminder emails for
            scheduled classes. Mindful Minds and Agape are enabled by default;
            other tenants can opt in here.
          </v-card-subtitle>

          <v-card-text v-if="loading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </v-card-text>

          <v-card-text v-else-if="config">
            <v-switch
              v-model="config.enabled"
              label="Reminders enabled"
              inset
              :hint="
                config.enabled
                  ? 'Reminders will fire automatically on the schedule below.'
                  : 'Reminders are currently OFF for this tenant. Existing manual sends still work.'
              "
              persistent-hint
            />

            <v-divider class="my-4" />

            <div class="text-subtitle-2 mb-2">Schedule</div>
            <v-radio-group
              v-model="config.scheduleMode"
              :disabled="!config.enabled"
              mandatory
            >
              <v-radio
                value="DAILY_MORNING"
                label="Daily morning batch (one fire per day per center)"
              />
              <v-radio
                value="PER_CLASS_LEAD"
                label="Per-class lead time (one fire per class)"
              />
            </v-radio-group>

            <v-text-field
              v-if="config.scheduleMode === 'DAILY_MORNING'"
              v-model="config.sendTimeLocal"
              label="Send time (tenant local time, HH:MM)"
              type="time"
              :disabled="!config.enabled"
              hint="Reminders fire when the tenant's local hour matches this time. Tenant timezone comes from Account.timezone."
              persistent-hint
              style="max-width: 260px"
            />
            <v-text-field
              v-if="config.scheduleMode === 'PER_CLASS_LEAD'"
              v-model.number="config.leadHours"
              label="Lead hours"
              type="number"
              min="1"
              :disabled="!config.enabled"
              hint="Send the reminder N hours before each class start time. Cron polls every 15 min."
              persistent-hint
              style="max-width: 260px"
            />

            <v-divider class="my-4" />

            <div class="text-subtitle-2 mb-2">Content</div>
            <v-text-field
              v-model="config.subjectTemplate"
              label="Subject template (optional — falls back to default)"
              :disabled="!config.enabled"
              placeholder="Reminder: {studentFirstName}'s class on {date}"
              persistent-hint
              hint="Placeholders: {parentFirstName} {studentFirstName} {date} {centerName} {accountName} {meetingLink}"
            />
            <v-textarea
              v-model="config.bodyTemplate"
              label="Body template (optional — falls back to default)"
              :disabled="!config.enabled"
              rows="6"
              auto-grow
              persistent-hint
              hint="Same placeholders as subject. {meetingLink} renders empty for students without a link."
            />

            <v-switch
              v-model="config.includeMeetingLink"
              label="Include meeting link when set"
              :disabled="!config.enabled"
              hint="Off = ignore the student's meeting_link even if present (placeholder renders empty)."
              persistent-hint
              inset
              class="mt-2"
            />
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="reload" :disabled="saving">Discard</v-btn>
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="!config"
              @click="save"
            >
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  getAccount,
  getReminderConfig,
  updateReminderConfig,
} from "@/services/accountServices";

/**
 * Phase 5 — per-tenant reminder pipeline configuration.
 *
 * One-tenant-per-view (the logged-in admin's own account). Multi-tenant
 * admins switch accounts elsewhere in the app, so we don't expose an
 * account-picker here.
 */
export default {
  name: "ReminderSettings",
  data: () => ({
    loading: true,
    saving: false,
    accountId: null,
    config: null,
  }),
  async mounted() {
    await this.reload();
  },
  methods: {
    async reload() {
      this.loading = true;
      try {
        const account = await getAccount();
        this.accountId = account.id;
        this.config = await getReminderConfig(this.accountId);
        // Backend returns sendTimeLocal as "HH:MM:SS"; v-text-field type=time
        // expects "HH:MM". Trim seconds at load + re-append on save.
        if (this.config.sendTimeLocal && this.config.sendTimeLocal.length > 5) {
          this.config.sendTimeLocal = this.config.sendTimeLocal.substring(0, 5);
        }
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to load reminder settings";
        this.$store.dispatch("messages/addErrorMessage", msg);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const payload = { ...this.config };
        if (payload.sendTimeLocal && payload.sendTimeLocal.length === 5) {
          payload.sendTimeLocal = `${payload.sendTimeLocal}:00`;
        }
        await updateReminderConfig(this.accountId, payload);
        this.$store.dispatch(
          "messages/addSuccessMessage",
          "Reminder settings saved",
        );
        await this.reload();
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to save reminder settings";
        this.$store.dispatch("messages/addErrorMessage", msg);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
